import cn from 'classnames';
import {InformerConfigState} from '../informerConfigReducer';
import {TemperatureUnit, SpeedUnit, PressureUnit, ClockType} from '@/common/constants/units';
import {getCssColor} from '@/common/components/ColorPicker';
import {localizationResources} from '@/informers/locales/content';
import domains from '@/domains.json';

const {routerKeywords, primaryLanguage: primaryDomainLanguage} = domains[DOMAIN_NAME];

const temperatureUnitCode: Record<TemperatureUnit, string> = {
	[TemperatureUnit.celsius]: 'c',
	[TemperatureUnit.fahrenheit]: 'f',
};

const speedUnitCode: Record<SpeedUnit, string> = {
	[SpeedUnit.ms]: 'ms',
	[SpeedUnit.kph]: 'kph',
	[SpeedUnit.mph]: 'ms',
};

const pressureUnitCode: Record<PressureUnit, string> = {
	[PressureUnit.mmHg]: 'mm-hg',
	[PressureUnit.dmHg]: 'dm-hg',
	[PressureUnit.hPa]: 'h-pa',
};

const clockTypeCode: Record<ClockType, string> = {
	[ClockType['24h']]: '24',
	[ClockType['12h']]: '12',
};

const makeStyle = (css: Record<string, {value: number | string | null; unit?: string}>) => {
	const styleValue = Object.entries(css).reduce((style, [rule, {value, unit}]) => {
		if (value === null) {
			return style;
		}
		return `${style}${rule}:${value}${unit || ''};`;
	}, '');

	if (styleValue) {
		return `style="${styleValue}"`;
	}

	return '';
};

export function informerCssUrlGenerator() {
	return `/resources/informer/css/informer.css`;
}

export function informerCodeGenerator(config: InformerConfigState, minify?: boolean) {
	const {
		locations,
		sizeType,
		layout,
		width,
		language,
		showWindSpeed,
		showHumidity,
		showPressure,
		showPrecipitation,
		timeFormat,
		temperatureUnit,
		pressureUnit,
		windSpeedUnit,
		fontFamily,
		theme,
		customFirstBgColor,
		customSecondBgColor,
		customFirstTextColor,
		customSecondTextColor,
		customIconsColor,
	} = config;

	let fixedWidth: number | null = width;
	if (sizeType === 'adaptive') {
		fixedWidth = null;
	} else if (locations.length > 1 && layout === 'horizontal') {
		fixedWidth = width * locations.length;
	}

	const domainName = DOMAIN_NAME;
	const domainOrigin = location.origin; // TODO development only, with prod mode use protocol with the domainName instead location.origin
	const iconsPath = `${domainOrigin}/resources/informer/assets/icons`;

	const linkLang = primaryDomainLanguage !== language ? `/${language}` : '';
	const footerLink =
		locations.length === 1
			? `${domainOrigin}${linkLang}/${routerKeywords.weather}/${locations[0].id}/${routerKeywords['10days']}`
			: `${domainOrigin}${linkLang}`;

	const texts = localizationResources[language];

	// TODO use png icons when non custom theme

	let code = `
		<div
			class="${cn(
				'sin-informer',
				`sin-informer_font-${fontFamily}`,
				theme === 'light' && 'sin-informer_theme-light',
				theme === 'dark' && 'sin-informer_theme-dark',
			)}"
			${makeStyle({
				width: {value: fixedWidth, unit: 'px'},
				'background-color': {value: theme === 'custom' ? getCssColor(customFirstBgColor) : null},
				color: {value: theme === 'custom' ? getCssColor(customFirstTextColor) : null},
			})}
			data-lang="${language}">
			<div class="sin-informer__header">
				<a class="sin-informer__logo-link" href="${domainOrigin}" target="_blank" rel="nofollow">
					<img class="sin-informer__logo-image" width="66" height="20" srcset="${iconsPath}/logo.png, ${iconsPath}/logo2x.png 2x" src="${iconsPath}/logo.png" alt="Sinoptik - logo">
				</a>
				<p class="sin-informer__date">${texts.datePlaceholder}</p>
				<p
					class="sin-informer__time"
					data-format="${clockTypeCode[timeFormat]}"
					${makeStyle({color: {value: theme === 'custom' ? getCssColor(customSecondTextColor) : null}})}>
					<span
						class="sin-informer__time-icon"
						${makeStyle({color: {value: theme === 'custom' ? getCssColor(customIconsColor) : null}})}>
					</span>
				</p>
			</div>
			<div class="${cn('sin-informer__main', layout === 'horizontal' && 'sin-informer__main_inline')}">
			${locations
				.map(
					location => `
						<a
							class="sin-informer__entry"
							href="${domainOrigin}${linkLang}/${routerKeywords.weather}/${location.id}"
							target="_blank"
							rel="nofollow">
								<p
									class="sin-informer__location"
									${makeStyle({'background-color': {value: theme === 'custom' ? getCssColor(customSecondBgColor) : null}})}>
									${location.title}
								</p>
								<div class="sin-informer__primary" style="display: none">
									<p
										class="sin-informer__local-time"
										${makeStyle({color: {value: theme === 'custom' ? getCssColor(customSecondTextColor) : null}})}
									></p>
									<p
										class="sin-informer__temp"
										data-unit="${temperatureUnitCode[temperatureUnit]}"
										${makeStyle({color: {value: theme === 'custom' ? getCssColor(customIconsColor) : null}})}
									></p>
									<div class="sin-informer__condition" data-icon-path="${iconsPath}/conditions"></div>
								</div>
								<div class="sin-informer__secondary" style="display: none">
									${
										showWindSpeed
											? `
												<p
													class="sin-informer__marker sin-informer__marker_wind"
													data-unit="${speedUnitCode[windSpeedUnit]}"
													data-suffix="${texts.speedUnit[windSpeedUnit]}"
													data-directions="${texts.windDirections}"
													title="${texts.weatherParams.wind}">
													<span
														class="sin-informer__marker-icon"
														${makeStyle({color: {value: theme === 'custom' ? getCssColor(customIconsColor) : null}})}>
													</span>
												</p>
											`
											: ''
									}
									${
										showHumidity
											? `
												<p
													class="sin-informer__marker sin-informer__marker_humidity"
													title="${texts.weatherParams.humidity}">
													<span
														class="sin-informer__marker-icon"
														${makeStyle({color: {value: theme === 'custom' ? getCssColor(customIconsColor) : null}})}>
													</span>
												</p>
											`
											: ''
									}
									${
										showPressure
											? `
												<p
													class="sin-informer__marker sin-informer__marker_pressure"
													data-unit="${pressureUnitCode[pressureUnit]}"
													data-suffix="${texts.pressureUnit[pressureUnit]}"
													title="${texts.weatherParams.pressure}">
													<span
														class="sin-informer__marker-icon"
														${makeStyle({color: {value: theme === 'custom' ? getCssColor(customIconsColor) : null}})}>
													</span>
												</p>
											`
											: ''
									}
									${
										showPrecipitation
											? `
												<p
													class="sin-informer__marker sin-informer__marker_precipitation"
													title="${texts.weatherParams.precipitation}">
													<span
														class="sin-informer__marker-icon"
														${makeStyle({color: {value: theme === 'custom' ? getCssColor(customIconsColor) : null}})}>
													</span>
												</p>
											`
											: ''
									}
								</div>
							</a>
					`,
				)
				.join('\n')}
			</div>
			<div
				class="sin-informer__footer"
				${makeStyle({color: {value: theme === 'custom' ? getCssColor(customSecondTextColor) : null}})}>
				${locations.length === 1 ? texts.goToTenDaysWeather : texts.goToWeather}
				<a
					class="sin-informer__domain-link"
					href="${footerLink}"
					target="_blank"
					rel="nofollow"
					${makeStyle({color: {value: theme === 'custom' ? getCssColor(customFirstTextColor) : null}})}>
					${domainName}
				</a>
			</div>
		</div>
	`;

	if (minify) {
		code = code
			.replace(/\n+/g, '')
			.replace(/\t+/g, ' ')
			.replace(/ +/g, ' ')
			.replace(/>\s+</g, '><')
			.replace(/ +>/g, '>')
			.replace(/< +/g, '<');
	}

	return code;
}
